import dynamic from 'next/dynamic';
import { TasticRegistry } from './types';

// Keep the LCP-critical hero eager: its hydration JS stays in the main chunk so
// nothing about the LCP render path changes.
import HeroTastic from './content/hero';
import HeroBannerTastic from './hero-banner';

// Code-split the remaining tastics (same dynamic() pattern as those below) so the
// initial route bundle only downloads/executes the components a page actually
// renders. dynamic() keeps ssr:true by default, so server HTML output is unchanged.
const AnnouncementBarTastic = dynamic(() => import('./bar/announcment'));
const CategorySliderTastic = dynamic(() => import('./category-slider'));
const Tile = dynamic(() => import('./content/tile'));
const TilesGroupTastic = dynamic(() => import('./content/tiles-group'));
const ContentSliderTastic = dynamic(() => import('./content-slider'));
const CuratedSliderTastic = dynamic(() => import('./curated-slider'));
const GMSBFooterTastic = dynamic(() => import('./gmsb-footer'));
const Footer = dynamic(() => import('./footer'));
const Header = dynamic(() => import('./gmsb-header'));
const NotFoundTastic = dynamic(() => import('./not-found'));
const ProductSlider = dynamic(() => import('./products/slider'));
const SpacerTastic = dynamic(() => import('./spacer'));
const ProductCategorySliderTastic = dynamic(() => import('./product-category-slider'));
const StripeIframeTastic = dynamic(() => import('./stripe-iframe'));
const PrintBuilderIframe = dynamic(() => import('./print-builder-iframe'));
const RecentSearchesTastic = dynamic(() => import('./recent-searches'));
const OccasionBannerTastic = dynamic(() => import('./occasion-banner'));
const FavoriteTastic = dynamic(() => import('./favorites'));
const JustForYouTastic = dynamic(() => import('./just-for-you'));
const ContactUsTastic = dynamic(() => import('./contact-us'));
const IntercomEmailSupportTastic = dynamic(() => import('./intercom-email-support'));
const PastOrdersTastic = dynamic(() => import('./past-orders'));
const TextOnlyBannerTastic = dynamic(() => import('./text-only-banner'));
const TNCTextOnlyBannerTastic = dynamic(() => import('./tnc-text-only-banner'));
const Markdown = dynamic(() => import('./markdown'));
const Banner = dynamic(() => import('./banner'));
const AccountDetails = dynamic(() => import('./account/details'));
const AccountOrdersHistory = dynamic(() => import('./account/orders'));
const Checkout = dynamic(() => import('./checkout'));
const ThankYou = dynamic(() => import('./thank-you'));
const ProductDetails = dynamic(() => import('./products/details'));
const ProductList = dynamic(() => import('./products/product-list'));
const ProductListAlgolia = dynamic(() => import('./products/product-list-algolia'));
const SimilarProducts = dynamic(() => import('./products/similar-products'));
const OtherProducts = dynamic(() => import('./products/other-products'));
const PromotionalProductBannerTastic = dynamic(() => import('./promotional-products-banner'));
const Showcase = dynamic(() => import('./showcase'));
const Cart = dynamic(() => import('./cart'));
const CategoryBar = dynamic(() => import('./category'));
const PromotionalCategoryGridTastic = dynamic(() => import('./promotional-category-grid'));
const BrandPromiseTastic = dynamic(() => import('./brand-promise'));
const MyPhotosWidgetTastic = dynamic(() => import('./my-photos-widget'));
const MyProjectTastic = dynamic(() => import('./my-project'));

const StoreFinder = dynamic(() => import('./store-finder'));
const RelatedProducts = dynamic(() => import('./products/related-products'));
const SubCategoriesListTastic = dynamic(() => import('./sub-categories-list'));
const AddToCartConfirmationTastic = dynamic(() => import('./add-to-cart-confirmation'));
const OrderDetailsTastic = dynamic(() => import('./order-details'));
const PersonalizedProductsTastic = dynamic(() => import('./personalized-products'));
const BreadcrumbTasticCLP = dynamic(() => import('./breadcrumbsclp'));
const SaleProducts = dynamic(() => import('./products/sale-products'));
const YotpoReviewTastic = dynamic(() => import('./yotpo-review'));
const YotpoReviewCarouselTastic = dynamic(() => import('./yotpo-review-carousel'));
const MerchandisingRailTastic = dynamic(() => import('./merchandising-rail'));

export const tastics = {
  'commercetools/ui/checkout': Checkout,
  'commercetools/ui/thank-you': ThankYou,
  'commercetools/ui/footer': Footer,
  'commercetools/ui/gmsb-footer': GMSBFooterTastic,
  'commercetools/ui/gmsb-header': Header,
  'commercetools/ui/content/tile': Tile,
  'commercetools/ui/content/hero': HeroTastic,
  'commercetools/ui/content/spacer': SpacerTastic,
  'commercetools/ui/content/showcase': Showcase,
  'commercetools/ui/content/markdown': Markdown,
  'commercetools/ui/content/banner': Banner,
  'commercetools/ui/cart': Cart,
  'commercetools/ui/products/details': ProductDetails,
  'commercetools/ui/products/product-list': ProductList,
  'commercetools/ui/products/product-list-algolia': ProductListAlgolia,
  'commercetools/ui/products/slider': ProductSlider,
  'commercetools/ui/products/similar-products': SimilarProducts,
  'commercetools/ui/products/other-products': OtherProducts,
  'commercetools/ui/promotional-products-banner': PromotionalProductBannerTastic,
  'commercetools/ui/account/details': AccountDetails,
  'commercetools/ui/account/orders': AccountOrdersHistory,
  'commercetools/ui/category-slider': CategorySliderTastic,
  'commercetools/ui/curated-slider': CuratedSliderTastic,
  'commercetools/ui/content/announcement-bar': AnnouncementBarTastic,
  'commercetools/ui/content/content-slider': ContentSliderTastic,
  'commercetools/ui/content/tiles-group': TilesGroupTastic,
  'commercetools/ui/product-category-slider': ProductCategorySliderTastic,
  'commercetools/ui/hero-banner': HeroBannerTastic,
  'commercetools/ui/not-found': NotFoundTastic,
  'commercetools/ui/categorybar': CategoryBar,
  'commercetools/ui/recentsearches': RecentSearchesTastic,
  'commercetools/ui/content/gmsb-featured-product-banner': PromotionalCategoryGridTastic,
  'commercetools/ui/content/gmsb-brand-promise': BrandPromiseTastic,
  'commercetools/ui/content/my-photos-widget': MyPhotosWidgetTastic,
  'commercetools/ui/content/gmsb-stripe-iframe': StripeIframeTastic,
  'commercetools/ui/content/gmsb-dynamic': PrintBuilderIframe,
  'commercetools/ui/content/gmsb-store-finder': StoreFinder,
  'commercetools/ui/products/related-products': RelatedProducts,
  'commercetools/ui/categories/list': SubCategoriesListTastic,
  'commercetools/ui/cart-confirmation': AddToCartConfirmationTastic,
  'commercetools/ui/occasion-banner': OccasionBannerTastic,
  'commercetools/ui/order-details': OrderDetailsTastic,
  'commercetools/ui/personalized-products-recommendations': PersonalizedProductsTastic,
  'commercetools/ui/breadcrumbsclp': BreadcrumbTasticCLP,
  'commercetools/ui/products/sale-products': SaleProducts,
  'commercetools/ui/favorites': FavoriteTastic,
  'commercetools/ui/past-orders': PastOrdersTastic,
  'commercetools/ui/myProject': MyProjectTastic,
  'commercetools/ui/just-for-you': JustForYouTastic,
  'commercetools/ui/text-only-banner': TextOnlyBannerTastic,
  'commercetools/ui/text-only-banner-for-terms': TNCTextOnlyBannerTastic,
  'commercetools/ui/contact-us': ContactUsTastic,
  'commercetools/ui/emailus': IntercomEmailSupportTastic,
  'commercetools/ui/yotpo-review': YotpoReviewTastic,
  'commercetools/ui/yotpo-review-carousel': YotpoReviewCarouselTastic,
  'commercetools/ui/merchandising-rail': MerchandisingRailTastic,
} as TasticRegistry;
